import { FileSpreadsheet } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';

type Props = {
  open: boolean;
  onClose: () => void;
  entityLabel: string;
  columns: ListColumnCatalogItem[];
  value: Record<string, boolean>;
  onChange: (columnId: string, visible: boolean) => void;
  onReset: () => void;
};

export function ConfigureListColumnsModal({
  open,
  onClose,
  entityLabel,
  columns,
  value,
  onChange,
  onReset,
}: Props) {
  const standard = columns.filter((c) => !c.optional);
  const optional = columns.filter((c) => c.optional);

  const row = (c: ListColumnCatalogItem) => (
    <label
      key={c.id}
      className="flex items-center gap-3 py-2 px-2 rounded-lg hover:vscode-bg-hover cursor-pointer"
    >
      <input
        type="checkbox"
        className="rounded border-gray-600"
        checked={value[c.id] === true}
        onChange={(e) => onChange(c.id, e.target.checked)}
      />
      <span className="text-sm vscode-text-primary">{c.label}</span>
    </label>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure columns"
      subtitle={`Show or hide columns for ${entityLabel}`}
      icon={<FileSpreadsheet className="w-4 h-4" />}
      size="md"
    >
      <div className="max-h-[min(60vh,420px)] overflow-y-auto overflow-x-hidden pr-1 -mr-1 space-y-4">
        {standard.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">
              Columns
            </p>
            <div className="border vscode-border rounded-lg divide-y divide-[var(--vscode-border-primary)]">
              {standard.map(row)}
            </div>
          </div>
        )}
        {optional.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">
              Optional fields
            </p>
            <p className="text-xs vscode-text-secondary mb-2">
              Turn these on to add data that stays hidden by default.
            </p>
            <div className="border vscode-border rounded-lg divide-y divide-[var(--vscode-border-primary)]">
              {optional.map(row)}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap justify-between gap-2 pt-4 border-t vscode-border mt-4">
        <button type="button" className="btn-secondary text-xs" onClick={onReset}>
          Reset to defaults
        </button>
        <button type="button" className="btn-primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/** Hide table cells when column is off (use with Configure). */
export function colClass(visible: boolean, base = ''): string {
  return visible ? base : `${base} hidden`.trim();
}
